import { makeAutoObservable, runInAction, reaction } from "mobx";
import type { RootStore } from "@intrasync/stores";
import { downloadFile } from "../shared/downloadFile";

/**
 * Payment Methods per spec 07-accounts-payable.md
 */
export type PaymentMethod = "check" | "ach" | "wire";

/**
 * Payment Status Flow
 */
export type PaymentStatus = "pending" | "scheduled" | "processing" | "completed" | "failed" | "voided";

/**
 * Invoice Status (Phase 1 - 1.1)
 * Corrected status values for AP workflow
 */
export type InvoiceStatus = "pending_approval" | "approved" | "paid";

/**
 * NACHA File Status
 */
export type NACHAStatus = "pending" | "uploaded" | "processed";

/**
 * ACH Account Type
 */
export type ACHAccountType = "checking" | "savings";

/**
 * Aging Bucket Type (Phase 1 - 1.2)
 */
export type AgingBucket = "current" | "1-30" | "31-60" | "61-90" | "90+";

/**
 * Invoice Attachment (Phase 1 - 1.3)
 */
export interface InvoiceAttachment {
  id: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  uploadedAt: Date;
  uploadedBy: string;
  url: string;
}

/**
 * Invoice pending payment
 */
export interface PayableInvoice {
  id: string;
  invoiceNumber: string;
  vendorId: string;
  vendorName: string;
  invoiceDate: string;
  dueDate: string;
  amount: number;
  amountDue: number;
  status: InvoiceStatus; // Phase 1 - 1.1: Typed status
  plantId: string;
  plantCode: string;
  poNumber?: string;
  isOverdue: boolean;
  daysOverdue: number;
  agingBucket: AgingBucket; // Phase 1 - 1.2: Aging bucket
  attachments?: InvoiceAttachment[]; // Phase 1 - 1.3: PDF attachments
}

/**
 * Payment Invoice link (for batch payments)
 */
export interface PaymentInvoice {
  invoiceId: string;
  invoiceNumber: string;
  amount: number;
}

/**
 * Payment record
 */
export interface Payment {
  id: string;
  paymentNumber: string;
  paymentMethod: PaymentMethod;
  paymentDate: string;
  vendorId: string;
  vendorName: string;
  invoices: PaymentInvoice[];
  amount: number;
  status: PaymentStatus;
  bankAccountId: string;
  bankAccountName?: string;
  nachaFileId?: string;
  checkNumber?: number;
  achReference?: string;
  memo?: string;
  createdBy: string;
  createdByName?: string;
  createdAt: Date;
  processedAt?: Date;
  voidedAt?: Date;
  voidedBy?: string;
  voidReason?: string;
}

/**
 * Vendor ACH Info (AP-08)
 */
export interface VendorACHInfo {
  id: string;
  vendorId: string;
  vendorName?: string;
  routingNumber: string;
  accountNumberMasked: string; // Only last 4 digits shown
  accountType: ACHAccountType;
  accountName: string;
  isVerified: boolean;
  verifiedAt?: Date;
  verifiedBy?: string;
}

/**
 * Vendor Default GL (AP-04)
 */
export interface VendorDefaultGL {
  vendorId: string;
  vendorName?: string;
  expenseAccountId: string;
  expenseAccountName?: string;
  assetAccountId?: string;
  assetAccountName?: string;
  notes?: string;
}

/**
 * NACHA File (AP-01)
 */
export interface NACHAFile {
  id: string;
  fileName: string;
  createdAt: Date;
  totalAmount: number;
  paymentCount: number;
  status: NACHAStatus;
  payments: Payment[];
  fileContent?: string;
}

/**
 * Bank Account for payments
 */
export interface BankAccount {
  id: string;
  name: string;
  accountNumber: string;
  routingNumber: string;
  nextCheckNumber: number;
  nextACHNumber: number;
}

/**
 * Payment Batch for creating multiple payments
 */
export interface PaymentBatch {
  id: string;
  paymentMethod: PaymentMethod;
  bankAccountId: string;
  paymentDate: string;
  invoices: PayableInvoice[];
  totalAmount: number;
}

/**
 * Quick Payment form data (AP-05)
 */
export interface QuickPaymentFormData {
  vendorId: string;
  paymentMethod: PaymentMethod;
  bankAccountId: string;
  amount: number;
  paymentDate: string;
  glAccountId: string;
  description: string;
  memo: string;
}

/**
 * Accounts Payable Store
 *
 * Manages AP data per spec 07-accounts-payable.md
 * - NACHA file generation (AP-01)
 * - ACH payment tracking (AP-02)
 * - Check printing (AP-03)
 * - Vendor default GL (AP-04)
 * - Quick payment (AP-05)
 * - Payment scheduling (AP-07)
 * - Vendor ACH info (AP-08)
 * - Payment batch processing (AP-09)
 */
class AccountsPayableStore {
  rootStore: RootStore;

  // Data - bankAccounts kept local until bank-accounts service is available
  bankAccounts: BankAccount[] = [];

  // Current batch
  currentBatch: PaymentBatch | null = null;
  selectedInvoiceIds: string[] = [];

  // Filters
  companyFilter: string | null = null;
  vendorFilter: string = "";
  statusFilter: PaymentStatus | "all" = "all";
  methodFilter: PaymentMethod | "all" = "all";
  dateRangeStart: string = "";
  dateRangeEnd: string = "";
  showOverdueOnly: boolean = false;
  // Phase 1 - 1.1: Invoice status filter
  invoiceStatusFilter: InvoiceStatus | "all" = "all";
  // Phase 1 - 1.2: Aging bucket filter
  agingBucketFilter: AgingBucket | "all" = "all";

  // Advanced Search (matching AR module pattern)
  advancedSearch = {
    isOpen: false,
    quickSearch: "",
    invoice: "",
    vendor: "",
    poNumber: "",
    dateFrom: "",
    dateTo: "",
    exactAmount: "",
    minAmount: "",
    maxAmount: "",
  };

  // UI State
  loading = false;
  error: string | null = null;
  selectedPayment: Payment | null = null;
  selectedNACHAFile: NACHAFile | null = null;

  // Quick payment form
  quickPaymentForm: QuickPaymentFormData = this.getEmptyQuickPaymentForm();

  // ACH Form for editing vendor info
  achFormData: Partial<VendorACHInfo> = {};

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });

    // Sync company filter with global AppLayout selection
    reaction(
      () => rootStore.appLayoutStore?.selectedCompanyId,
      (companyId) => {
        this.companyFilter = companyId;
      },
      { fireImmediately: true }
    );
  }

  // ==================== ServiceStore Access ====================

  get payableInvoicesServiceStore() {
    return this.rootStore.serviceStores?.["ap-invoices"];
  }

  get paymentsServiceStore() {
    return this.rootStore.serviceStores?.["ap-payments"];
  }

  get nachaFilesServiceStore() {
    return this.rootStore.serviceStores?.["nacha-files"];
  }

  get vendorACHInfoServiceStore() {
    return this.rootStore.serviceStores?.["vendor-ach-info"];
  }

  get vendorDefaultGLsServiceStore() {
    return this.rootStore.serviceStores?.["vendor-default-gl"];
  }

  // ==================== Computed: Data from ServiceStores ====================

  get payableInvoices(): PayableInvoice[] {
    return (this.payableInvoicesServiceStore?.entities as PayableInvoice[]) || [];
  }

  get payments(): Payment[] {
    return (this.paymentsServiceStore?.entities as Payment[]) || [];
  }

  get nachaFiles(): NACHAFile[] {
    return (this.nachaFilesServiceStore?.entities as NACHAFile[]) || [];
  }

  get vendorACHInfo(): VendorACHInfo[] {
    return (this.vendorACHInfoServiceStore?.entities as VendorACHInfo[]) || [];
  }

  get vendorDefaultGLs(): VendorDefaultGL[] {
    return (this.vendorDefaultGLsServiceStore?.entities as VendorDefaultGL[]) || [];
  }

  /**
   * Estimated next ACH number based on loaded payments.
   * The backend auto-assigns the real number on create; this is for display only.
   */
  get nextAchNumber(): string {
    const count = this.payments.filter(
      (p) => p.paymentMethod === "ach" && (p as any).achNumber
    ).length;
    return `ACH-${String(count + 1).padStart(4, "0")}`;
  }

  // ==================== Computed Properties ====================

  /**
   * Filtered payable invoices
   */
  get filteredPayableInvoices(): PayableInvoice[] {
    let filtered = [...this.payableInvoices];
    const search = this.advancedSearch;

    // Quick search (from header search bar)
    if (search.quickSearch) {
      const q = search.quickSearch.toLowerCase();
      filtered = filtered.filter(
        (i) =>
          i.vendorName.toLowerCase().includes(q) ||
          i.invoiceNumber.toLowerCase().includes(q) ||
          (i.poNumber && i.poNumber.toLowerCase().includes(q))
      );
    }

    // Legacy vendor filter (kept for backward compatibility)
    if (this.vendorFilter) {
      const q = this.vendorFilter.toLowerCase();
      filtered = filtered.filter(
        (i) =>
          i.vendorName.toLowerCase().includes(q) ||
          i.invoiceNumber.toLowerCase().includes(q)
      );
    }

    // Advanced search: Invoice number
    if (search.invoice) {
      const q = search.invoice.toLowerCase();
      filtered = filtered.filter((i) => i.invoiceNumber.toLowerCase().includes(q));
    }

    // Advanced search: Vendor name
    if (search.vendor) {
      const q = search.vendor.toLowerCase();
      filtered = filtered.filter((i) => i.vendorName.toLowerCase().includes(q));
    }

    // Advanced search: PO number
    if (search.poNumber) {
      const q = search.poNumber.toLowerCase();
      filtered = filtered.filter((i) => i.poNumber && i.poNumber.toLowerCase().includes(q));
    }

    // Advanced search: Date range
    if (search.dateFrom) {
      filtered = filtered.filter((i) => i.invoiceDate >= search.dateFrom);
    }
    if (search.dateTo) {
      filtered = filtered.filter((i) => i.invoiceDate <= search.dateTo);
    }

    // Advanced search: Amount filters
    if (search.exactAmount) {
      const amount = parseFloat(search.exactAmount);
      if (!isNaN(amount)) {
        filtered = filtered.filter((i) => Math.abs(i.amountDue - amount) < 0.01);
      }
    } else {
      if (search.minAmount) {
        const min = parseFloat(search.minAmount);
        if (!isNaN(min)) {
          filtered = filtered.filter((i) => i.amountDue >= min);
        }
      }
      if (search.maxAmount) {
        const max = parseFloat(search.maxAmount);
        if (!isNaN(max)) {
          filtered = filtered.filter((i) => i.amountDue <= max);
        }
      }
    }

    if (this.showOverdueOnly) {
      filtered = filtered.filter((i) => i.isOverdue);
    }

    // Phase 1 - 1.1: Invoice status filter
    if (this.invoiceStatusFilter !== "all") {
      filtered = filtered.filter((i) => i.status === this.invoiceStatusFilter);
    }

    // Phase 1 - 1.2: Aging bucket filter
    if (this.agingBucketFilter !== "all") {
      filtered = filtered.filter((i) => i.agingBucket === this.agingBucketFilter);
    }

    return filtered.sort((a, b) => {
      // Sort by due date (oldest first)
      return a.dueDate.localeCompare(b.dueDate);
    });
  }

  /**
   * Filtered payments
   */
  get filteredPayments(): Payment[] {
    let filtered = [...this.payments];

    if (this.statusFilter !== "all") {
      filtered = filtered.filter((p) => p.status === this.statusFilter);
    }

    if (this.methodFilter !== "all") {
      filtered = filtered.filter((p) => p.paymentMethod === this.methodFilter);
    }

    if (this.dateRangeStart) {
      filtered = filtered.filter((p) => p.paymentDate >= this.dateRangeStart);
    }

    if (this.dateRangeEnd) {
      filtered = filtered.filter((p) => p.paymentDate <= this.dateRangeEnd);
    }

    return filtered.sort((a, b) => b.paymentDate.localeCompare(a.paymentDate));
  }

  /**
   * Selected invoices for batch
   */
  get selectedInvoices(): PayableInvoice[] {
    return this.payableInvoices.filter((i) =>
      this.selectedInvoiceIds.includes(i.id)
    );
  }

  /**
   * Total selected amount
   */
  get selectedTotalAmount(): number {
    return this.selectedInvoices.reduce((sum, i) => sum + i.amountDue, 0);
  }

  /**
   * Summary metrics (Phase 1 - 1.2: Enhanced with aging buckets)
   */
  get metrics() {
    const overdueInvoices = this.payableInvoices.filter((i) => i.isOverdue);
    const totalPayable = this.payableInvoices.reduce((sum, i) => sum + i.amountDue, 0);
    const overdueAmount = overdueInvoices.reduce((sum, i) => sum + i.amountDue, 0);

    // Aging bucket calculations
    const currentInvoices = this.payableInvoices.filter((i) => i.agingBucket === "current");
    const aging1to30 = this.payableInvoices.filter((i) => i.agingBucket === "1-30");
    const aging31to60 = this.payableInvoices.filter((i) => i.agingBucket === "31-60");
    const aging61to90 = this.payableInvoices.filter((i) => i.agingBucket === "61-90");
    const aging90plus = this.payableInvoices.filter((i) => i.agingBucket === "90+");

    return {
      totalPayable,
      invoiceCount: this.payableInvoices.length,
      overdueCount: overdueInvoices.length,
      overdueAmount,
      pendingPayments: this.payments.filter((p) => p.status === "pending").length,
      scheduledPayments: this.payments.filter((p) => p.status === "scheduled").length,
      pendingNACHA: this.nachaFiles.filter((f) => f.status === "pending").length,
      // Phase 1 - 1.2: Aging metrics
      aging: {
        current: {
          count: currentInvoices.length,
          amount: currentInvoices.reduce((sum, i) => sum + i.amountDue, 0),
        },
        "1-30": {
          count: aging1to30.length,
          amount: aging1to30.reduce((sum, i) => sum + i.amountDue, 0),
        },
        "31-60": {
          count: aging31to60.length,
          amount: aging31to60.reduce((sum, i) => sum + i.amountDue, 0),
        },
        "61-90": {
          count: aging61to90.length,
          amount: aging61to90.reduce((sum, i) => sum + i.amountDue, 0),
        },
        "90+": {
          count: aging90plus.length,
          amount: aging90plus.reduce((sum, i) => sum + i.amountDue, 0),
        },
      },
      // Phase 1 - 1.1: Status metrics
      byStatus: {
        pending_approval: this.payableInvoices.filter((i) => i.status === "pending_approval").length,
        approved: this.payableInvoices.filter((i) => i.status === "approved").length,
        paid: this.payableInvoices.filter((i) => i.status === "paid").length,
      },
    };
  }

  /**
   * Invoices grouped by vendor
   */
  get invoicesByVendor(): Record<string, PayableInvoice[]> {
    const grouped: Record<string, PayableInvoice[]> = {};
    this.payableInvoices.forEach((invoice) => {
      if (!grouped[invoice.vendorId]) {
        grouped[invoice.vendorId] = [];
      }
      grouped[invoice.vendorId].push(invoice);
    });
    return grouped;
  }

  // ==================== Filter Actions ====================

  setCompanyFilter = (companyId: string | null) => {
    this.companyFilter = companyId;
  };

  setVendorFilter = (value: string) => {
    this.vendorFilter = value;
  };

  setStatusFilter = (status: PaymentStatus | "all") => {
    this.statusFilter = status;
  };

  setMethodFilter = (method: PaymentMethod | "all") => {
    this.methodFilter = method;
  };

  setDateRange = (start: string, end: string) => {
    this.dateRangeStart = start;
    this.dateRangeEnd = end;
  };

  setShowOverdueOnly = (value: boolean) => {
    this.showOverdueOnly = value;
  };

  // Phase 1 - 1.1: Invoice status filter
  setInvoiceStatusFilter = (status: InvoiceStatus | "all") => {
    this.invoiceStatusFilter = status;
  };

  // Phase 1 - 1.2: Aging bucket filter with drill-through
  setAgingBucketFilter = (bucket: AgingBucket | "all") => {
    this.agingBucketFilter = bucket;
  };

  /**
   * Set aging bucket from clicking on summary card (Phase 1 - 1.2)
   */
  filterByAgingBucket = (bucket: AgingBucket) => {
    this.agingBucketFilter = bucket;
    // Clear other filters that might conflict
    this.showOverdueOnly = false;
  };

  // ==================== Advanced Search (matching AR pattern) ====================

  openAdvancedSearch = () => {
    this.advancedSearch.isOpen = true;
  };

  closeAdvancedSearch = () => {
    this.advancedSearch.isOpen = false;
  };

  updateAdvancedSearch = (field: keyof typeof this.advancedSearch, value: string | boolean) => {
    (this.advancedSearch as any)[field] = value;
  };

  clearAdvancedSearch = () => {
    this.advancedSearch = {
      isOpen: this.advancedSearch.isOpen,
      quickSearch: "",
      invoice: "",
      vendor: "",
      poNumber: "",
      dateFrom: "",
      dateTo: "",
      exactAmount: "",
      minAmount: "",
      maxAmount: "",
    };
  };

  get hasAdvancedSearchCriteria(): boolean {
    const s = this.advancedSearch;
    return !!(
      s.quickSearch ||
      s.invoice ||
      s.vendor ||
      s.poNumber ||
      s.dateFrom ||
      s.dateTo ||
      s.exactAmount ||
      s.minAmount ||
      s.maxAmount
    );
  }

  get advancedSearchFilterCount(): number {
    const s = this.advancedSearch;
    let count = 0;
    if (s.invoice) count++;
    if (s.vendor) count++;
    if (s.poNumber) count++;
    if (s.dateFrom || s.dateTo) count++;
    if (s.exactAmount || s.minAmount || s.maxAmount) count++;
    return count;
  }

  clearFilters = () => {
    this.companyFilter = null;
    this.vendorFilter = "";
    this.statusFilter = "all";
    this.methodFilter = "all";
    this.dateRangeStart = "";
    this.dateRangeEnd = "";
    this.showOverdueOnly = false;
    this.invoiceStatusFilter = "all";
    this.agingBucketFilter = "all";
    this.clearAdvancedSearch();
  };

  // ==================== Invoice Selection ====================

  toggleInvoiceSelection = (invoiceId: string) => {
    const index = this.selectedInvoiceIds.indexOf(invoiceId);
    if (index === -1) {
      this.selectedInvoiceIds.push(invoiceId);
    } else {
      this.selectedInvoiceIds.splice(index, 1);
    }
  };

  selectAllInvoices = () => {
    this.selectedInvoiceIds = this.filteredPayableInvoices.map((i) => i.id);
  };

  clearInvoiceSelection = () => {
    this.selectedInvoiceIds = [];
  };

  selectVendorInvoices = (vendorId: string) => {
    const vendorInvoices = this.payableInvoices
      .filter((i) => i.vendorId === vendorId)
      .map((i) => i.id);
    this.selectedInvoiceIds = [...new Set([...this.selectedInvoiceIds, ...vendorInvoices])];
  };

  // ==================== Payment Batch (AP-09) ====================

  /**
   * Create a payment batch from selected invoices
   */
  createPaymentBatch = (paymentMethod: PaymentMethod, bankAccountId: string) => {
    if (this.selectedInvoices.length === 0) return;

    const today = new Date().toISOString().split("T")[0];

    this.currentBatch = {
      id: `batch-${Date.now()}`,
      paymentMethod,
      bankAccountId,
      paymentDate: today,
      invoices: [...this.selectedInvoices],
      totalAmount: this.selectedTotalAmount,
    };

    this.rootStore.routerStore.openDialog({
      dialogName: "paymentBatch",
    });
  };

  /**
   * Process payment batch
   */
  processPaymentBatch = async () => {
    if (!this.currentBatch) return;

    this.loading = true;
    this.error = null;

    try {
      // Group invoices by vendor for batch
      const vendorGroups = new Map<string, PayableInvoice[]>();
      this.currentBatch.invoices.forEach((inv) => {
        if (!vendorGroups.has(inv.vendorId)) {
          vendorGroups.set(inv.vendorId, []);
        }
        vendorGroups.get(inv.vendorId)!.push(inv);
      });

      // Create payment for each vendor via ServiceStore
      const createPromises: Promise<any>[] = [];
      vendorGroups.forEach((invoices, vendorId) => {
        const vendor = invoices[0];
        const totalAmount = invoices.reduce((sum, i) => sum + i.amountDue, 0);

        createPromises.push(
          this.paymentsServiceStore?.create({
            paymentMethod: this.currentBatch!.paymentMethod,
            paymentDate: this.currentBatch!.paymentDate,
            vendorId,
            vendorName: vendor.vendorName,
            invoices: invoices.map((i) => ({
              invoiceId: i.id,
              invoiceNumber: i.invoiceNumber,
              amount: i.amountDue,
            })),
            amount: totalAmount,
            status: "scheduled",
            bankAccountId: this.currentBatch!.bankAccountId,
          }) ?? Promise.resolve()
        );
      });

      await Promise.all(createPromises);

      // Mark batch invoices as paid
      const patchPromises = this.currentBatch.invoices.map((inv) =>
        this.payableInvoicesServiceStore?.patch(inv.id, { status: "paid" }) ?? Promise.resolve()
      );
      await Promise.all(patchPromises);

      runInAction(() => {
        this.selectedInvoiceIds = [];
        this.currentBatch = null;
        this.loading = false;
      });

      this.rootStore.routerStore.closeDialog();
    } catch (err: any) {
      runInAction(() => {
        this.error = err.message || "Failed to process payment batch";
        this.loading = false;
      });
    }
  };

  // ==================== NACHA File Generation (AP-01) ====================

  /**
   * Generate NACHA file for ACH payments
   */
  generateNACHAFile = async () => {
    const achPayments = this.payments.filter(
      (p) => p.paymentMethod === "ach" && p.status === "scheduled"
    );

    if (achPayments.length === 0) {
      this.error = "No ACH payments ready for NACHA generation";
      return;
    }

    this.loading = true;

    try {
      const totalAmount = achPayments.reduce((sum, p) => sum + p.amount, 0);
      const fileName = `NACHA_${new Date().toISOString().split("T")[0]}_${Date.now()}.txt`;
      const nachaContent = this.generateNACHAContent(achPayments);

      // Create NACHA file via ServiceStore
      const nachaFile = await this.nachaFilesServiceStore?.create({
        fileName,
        totalAmount,
        paymentCount: achPayments.length,
        status: "pending",
        paymentIds: achPayments.map((p) => p.id),
        fileContent: nachaContent,
      });

      // Update payment statuses
      const patchPromises = achPayments.map((p) =>
        this.paymentsServiceStore?.patch(p.id, {
          status: "processing",
          nachaFileId: nachaFile?.id,
        }) ?? Promise.resolve()
      );
      await Promise.all(patchPromises);

      runInAction(() => {
        this.selectedNACHAFile = nachaFile as NACHAFile;
        this.loading = false;
      });

      this.rootStore.routerStore.openDialog({
        dialogName: "nachaPreview",
        dialogContext: nachaFile?.id,
      });
    } catch (err: any) {
      runInAction(() => {
        this.error = err.message || "Failed to generate NACHA file";
        this.loading = false;
      });
    }
  };

  /**
   * Generate NACHA file content (simplified format)
   */
  generateNACHAContent = (payments: Payment[]): string => {
    const today = new Date();
    const fileDate = today.toISOString().slice(2, 10).replace(/-/g, "");
    const fileTime = today.toTimeString().slice(0, 5).replace(":", "");

    let content = "";

    // File Header Record (1)
    content += `101 091000019 1234567890${fileDate}${fileTime}A094101TRAVIS GROUP           US BANK                        \n`;

    // Batch Header Record (5)
    content += `5200TRAVIS GROUP                        1234567890PPDPAYMENT   ${fileDate}   1091000010000001\n`;

    // Entry Detail Records (6)
    let entryCount = 0;
    const totalDebit = 0;
    let totalCredit = 0;

    payments.forEach((payment) => {
      const vendorACH = this.vendorACHInfo.find((v) => v.vendorId === payment.vendorId);
      if (vendorACH) {
        entryCount++;
        const amountCents = Math.round(payment.amount * 100)
          .toString()
          .padStart(10, "0");
        totalCredit += payment.amount * 100;

        content += `622${vendorACH.routingNumber}${vendorACH.accountNumberMasked.padEnd(17)}${amountCents}${payment.vendorId.slice(0, 15).padEnd(15)}${payment.vendorName.slice(0, 22).padEnd(22)}  0091000010${entryCount.toString().padStart(7, "0")}\n`;
      }
    });

    // Batch Control Record (8)
    content += `820000000${entryCount.toString().padStart(6, "0")}${totalDebit.toString().padStart(12, "0")}${totalCredit.toString().padStart(12, "0")}1234567890                         091000010000001\n`;

    // File Control Record (9)
    content += `9000001000001${entryCount.toString().padStart(8, "0")}${totalDebit.toString().padStart(12, "0")}${totalCredit.toString().padStart(12, "0")}                                       \n`;

    return content;
  };

  /**
   * Download NACHA file
   */
  downloadNACHAFile = (nachaFile: NACHAFile) => {
    if (!nachaFile.fileContent) return;

    downloadFile(nachaFile.fileContent, nachaFile.fileName, "text/plain");
  };

  /**
   * Mark NACHA file as uploaded
   */
  markNACHAUploaded = async (nachaFileId: string) => {
    const file = this.nachaFiles.find((f) => f.id === nachaFileId);
    if (!file) return;

    try {
      await this.nachaFilesServiceStore?.patch(nachaFileId, { status: "uploaded" });

      // Update associated payments to completed
      const patchPromises = file.payments.map((p) =>
        this.paymentsServiceStore?.patch(p.id, {
          status: "completed",
          processedAt: new Date(),
        }) ?? Promise.resolve()
      );
      await Promise.all(patchPromises);
    } catch {
      // Failed to mark NACHA as uploaded
    }
  };

  // ==================== Check Printing (AP-03) ====================

  /**
   * Open check print dialog
   */
  openCheckPrint = (paymentId: string) => {
    this.selectedPayment = this.payments.find((p) => p.id === paymentId) || null;
    if (this.selectedPayment) {
      this.rootStore.routerStore.openDialog({
        dialogName: "checkPrint",
        dialogContext: paymentId,
      });
    }
  };

  /**
   * Mark check as printed
   */
  markCheckPrinted = async (paymentId: string, checkNumber: number) => {
    try {
      await this.paymentsServiceStore?.patch(paymentId, {
        checkNumber,
        status: "completed",
        processedAt: new Date(),
      });
    } catch {
      // Failed to mark check as printed
    }
  };

  // ==================== Quick Payment (AP-05) ====================

  getEmptyQuickPaymentForm(): QuickPaymentFormData {
    return {
      vendorId: "",
      paymentMethod: "check",
      bankAccountId: "",
      amount: 0,
      paymentDate: new Date().toISOString().split("T")[0],
      glAccountId: "",
      description: "",
      memo: "",
    };
  }

  resetQuickPaymentForm = () => {
    this.quickPaymentForm = this.getEmptyQuickPaymentForm();
  };

  updateQuickPaymentField = <K extends keyof QuickPaymentFormData>(
    field: K,
    value: QuickPaymentFormData[K]
  ) => {
    this.quickPaymentForm[field] = value;

    // Auto-fill GL from vendor defaults
    if (field === "vendorId" && value) {
      const defaultGL = this.vendorDefaultGLs.find((v) => v.vendorId === value);
      if (defaultGL) {
        this.quickPaymentForm.glAccountId = defaultGL.expenseAccountId;
      }
    }
  };

  /**
   * Create quick payment (skips PO process)
   */
  createQuickPayment = async () => {
    this.loading = true;
    this.error = null;

    try {
      await this.paymentsServiceStore?.create({
        paymentMethod: this.quickPaymentForm.paymentMethod,
        paymentDate: this.quickPaymentForm.paymentDate,
        vendorId: this.quickPaymentForm.vendorId,
        invoices: [],
        amount: this.quickPaymentForm.amount,
        status: "pending",
        bankAccountId: this.quickPaymentForm.bankAccountId,
        memo: this.quickPaymentForm.memo,
        glAccountId: this.quickPaymentForm.glAccountId,
        description: this.quickPaymentForm.description,
      });

      runInAction(() => {
        this.resetQuickPaymentForm();
        this.loading = false;
      });

      this.rootStore.routerStore.closeDialog();
    } catch (err: any) {
      runInAction(() => {
        this.error = err.message || "Failed to create quick payment";
        this.loading = false;
      });
    }
  };

  // ==================== ACH Info Management (AP-08) ====================

  /**
   * Get vendor ACH info
   */
  getVendorACHInfo = (vendorId: string): VendorACHInfo | undefined => {
    return this.vendorACHInfo.find((v) => v.vendorId === vendorId);
  };

  /**
   * Save vendor ACH info
   */
  saveVendorACHInfo = async (data: Partial<VendorACHInfo>) => {
    this.loading = true;

    try {
      const existing = this.vendorACHInfo.find(
        (v) => v.vendorId === data.vendorId
      );

      if (existing) {
        await this.vendorACHInfoServiceStore?.patch(existing.id, data);
      } else {
        await this.vendorACHInfoServiceStore?.create({
          vendorId: data.vendorId,
          routingNumber: data.routingNumber || "",
          accountNumberMasked: data.accountNumberMasked || "****1234",
          accountType: data.accountType || "checking",
          accountName: data.accountName || "",
          isVerified: false,
        });
      }

      runInAction(() => {
        this.loading = false;
      });
    } catch (err: any) {
      runInAction(() => {
        this.error = err.message || "Failed to save ACH info";
        this.loading = false;
      });
    }
  };

  // ==================== Payment Void ====================

  /**
   * Void a payment
   */
  voidPayment = async (paymentId: string, reason: string) => {
    const payment = this.payments.find((p) => p.id === paymentId);
    if (!payment) return;

    this.loading = true;

    try {
      await this.paymentsServiceStore?.patch(paymentId, {
        status: "voided",
        voidedAt: new Date(),
        voidReason: reason,
      });

      runInAction(() => {
        this.loading = false;
      });

      this.rootStore.routerStore.closeDialog();
    } catch (err: any) {
      runInAction(() => {
        this.error = err.message || "Failed to void payment";
        this.loading = false;
      });
    }
  };

  // ==================== Helpers ====================

  getVendorById = (vendorId: string): { id: string; name: string } | undefined => {
    if (!vendorId) return undefined;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const vendorsStore = (this.rootStore as any).vendorsStore;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const vendor = vendorsStore?.entities?.find((v: any) => v.id === vendorId);
    return vendor ? { id: vendor.id, name: vendor.name || "Unknown" } : { id: vendorId, name: "Unknown" };
  };

  getPaymentMethodLabel = (method: PaymentMethod): string => {
    const labels: Record<PaymentMethod, string> = {
      check: "Check",
      ach: "ACH",
      wire: "Wire Transfer",
    };
    return labels[method] || method;
  };

  getStatusLabel = (status: PaymentStatus): string => {
    const labels: Record<PaymentStatus, string> = {
      pending: "Pending",
      scheduled: "Scheduled",
      processing: "Processing",
      completed: "Completed",
      failed: "Failed",
      voided: "Voided",
    };
    return labels[status] || status;
  };

  getStatusColor = (
    status: PaymentStatus
  ): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    const colors: Record<PaymentStatus, any> = {
      pending: "warning",
      scheduled: "info",
      processing: "primary",
      completed: "success",
      failed: "error",
      voided: "error",
    };
    return colors[status] || "default";
  };

  // Phase 1 - 1.1: Invoice status helpers
  getInvoiceStatusLabel = (status: InvoiceStatus): string => {
    const labels: Record<InvoiceStatus, string> = {
      pending_approval: "Pending Approval",
      approved: "Approved",
      paid: "Paid",
    };
    return labels[status] || status;
  };

  getInvoiceStatusColor = (
    status: InvoiceStatus
  ): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    const colors: Record<InvoiceStatus, any> = {
      pending_approval: "warning",
      approved: "success",
      paid: "info",
    };
    return colors[status] || "default";
  };

  // Phase 1 - 1.1: Update invoice status
  updateInvoiceStatus = async (invoiceId: string, status: InvoiceStatus) => {
    try {
      await this.payableInvoicesServiceStore?.patch(invoiceId, { status });
    } catch {
      // Failed to update invoice status
    }
  };

  // Phase 1 - 1.1: Approve invoice
  approveInvoice = (invoiceId: string) => {
    this.updateInvoiceStatus(invoiceId, "approved");
  };

  // Phase 1 - 1.3: Add attachment to invoice
  addInvoiceAttachment = async (invoiceId: string, attachment: Omit<InvoiceAttachment, "id">) => {
    const invoice = this.payableInvoices.find((i) => i.id === invoiceId);
    if (!invoice) return;

    const updatedAttachments = [
      ...(invoice.attachments || []),
      { ...attachment, id: `att-${Date.now()}` },
    ];

    try {
      await this.payableInvoicesServiceStore?.patch(invoiceId, {
        attachments: updatedAttachments,
      });
    } catch {
      // Failed to add attachment
    }
  };

  // Phase 1 - 1.3: Remove attachment from invoice
  removeInvoiceAttachment = async (invoiceId: string, attachmentId: string) => {
    const invoice = this.payableInvoices.find((i) => i.id === invoiceId);
    if (!invoice?.attachments) return;

    const updatedAttachments = invoice.attachments.filter((a) => a.id !== attachmentId);

    try {
      await this.payableInvoicesServiceStore?.patch(invoiceId, {
        attachments: updatedAttachments,
      });
    } catch {
      // Failed to remove attachment
    }
  };

  // Phase 1 - 1.3: Get attachments for invoice
  getInvoiceAttachments = (invoiceId: string): InvoiceAttachment[] => {
    const invoice = this.payableInvoices.find((i) => i.id === invoiceId);
    return invoice?.attachments || [];
  };

  // ==================== Data Loading ====================

  loadData = async () => {
    this.loading = true;
    this.error = null;

    try {
      await Promise.all([
        this.payableInvoicesServiceStore?.find({ query: { $limit: 200 } }),
        this.paymentsServiceStore?.find({ query: { $limit: 200 } }),
        this.nachaFilesServiceStore?.find({ query: { $limit: 200 } }),
        this.vendorACHInfoServiceStore?.find({ query: { $limit: 200 } }),
        this.vendorDefaultGLsServiceStore?.find({ query: { $limit: 200 } }),
        // TODO: Load bank accounts from bank-accounts ServiceStore when available
      ]);

      runInAction(() => {
        this.loading = false;
      });
    } catch (err: any) {
      runInAction(() => {
        this.error = err.message || "Failed to load AP data";
        this.loading = false;
      });
    }
  };

  reset = () => {
    this.clearFilters();
    this.clearInvoiceSelection();
    this.currentBatch = null;
    this.selectedPayment = null;
    this.selectedNACHAFile = null;
    this.resetQuickPaymentForm();
    this.error = null;
  };
}

export default AccountsPayableStore;
